export type CreditAgency = "Equifax" | "Callcredit";

export enum CreditCheckVisibility {
  Unknown = 0,
  UnableToView = 1,
  PartialView = 2,
  FullView = 3,
}

export interface CreditCheckSearchResponse {
  id: string;
}

export interface CreditCheckSummary {
  activeAccounts: number;
  activeAccountsTotalValue: number;
  worstIn12Months: string | number;
  worstIn36Months: string | number;
  defaults: number;
  cCJs: number;
  cCJTotalValue: number;
  searchesIn6Months: number;
  activePaydayLoans: number;
  activePaydayLoansTotalValue: number;
  revolvingCreditLimit: number;
  revolvingCreditUtilisation: number;
}

export interface CreditCheckPerson {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  surname?: string;
  dateOfBirth?: string;
  accountHolderType?: string;
}

export interface CreditAccountPaymentHistoryItem {
  date: string;
  status: string;
  payment: number;
  balance: number;
  creditLimit: number;
}

export interface CreditAccountMonthSlot {
  status: string;
  payment: number;
  balance: number;
  creditLimit: number;
  date: Date;
}

export interface CreditAccount {
  person: CreditCheckPerson;
  accountType: string;
  companyName: string;
  companyType: string;
  startDate: string;
  endDate: string | null;
  lastUpdateDate: string;
  creditLimit: number;
  currentBalance: number;
  startBalance: number;
  defaultBalance: number;
  defaultDate: string | null;
  lastThreePlusDate: string | null;
  numberOfPayments: number;
  paymentFrequency: string;
  paymentAmount: number;
  supplementaryInformation: string[];
  paymentHistory: CreditAccountPaymentHistoryItem[];
  monthSlots?: CreditAccountMonthSlot[];
}

export interface ElectoralRollItem {
  person: CreditCheckPerson;
  startDate: string;
  endDate: string | null;
}

export interface PreviousSearchItem {
  person: CreditCheckPerson;
  searchDate: string;
  companyType: string;
  searchType: string;
}

export interface CcjItem {
  person: CreditCheckPerson;
  satisfiedDate: string | null;
  value: number;
  courtDate: string;
  ccjType: string;
}

export interface NoticeOfCorrectionItem {
  person: CreditCheckPerson;
  createdDate: string;
  text: string;
  typeOfData: string;
}

export interface CifasItem {
  person: CreditCheckPerson;
  caseType: string;
  applicationDate: string;
  fraudDate: string;
}

export interface CreditCheckAddress {
  addressType: string;
  houseName?: string;
  houseNumber?: string;
  subBuilding?: string;
  street?: string;
  town?: string;
  postcode: string;
  accounts: CreditAccount[];
  filteredAccounts?: CreditAccount[];
  electoralRoll: ElectoralRollItem[];
  filteredElectoralRoll?: ElectoralRollItem[];
  cifas?: CifasItem[];
  filteredCifas?: CifasItem[];
  ccj: CcjItem[];
  filteredCcj?: CcjItem[];
  previousSearches: PreviousSearchItem[];
  filteredPreviousSearches?: PreviousSearchItem[];
  noticeOfCorrection: NoticeOfCorrectionItem[];
  filteredNoticeOfCorrection?: NoticeOfCorrectionItem[];
}

export interface CreditCheckDetail {
  id?: string;
  agency: string;
  score: number;
  searchPurpose: string;
  agencySearchDate: string;
  person: CreditCheckPerson;
  summary?: CreditCheckSummary;
  addresses: CreditCheckAddress[];
}

export interface CustomerResidentialAddress {
  addressId?: number;
  key?: string;
  buildingName?: string;
  houseName?: string;
  buildingNumber?: string;
  houseNumber?: string;
  subBuilding?: string;
  streetName?: string;
  street?: string;
  town?: string;
  postcode: string;
}

export interface CustomerInfo {
  customerId: number;
  firstName: string;
  middleName?: string;
  surname: string;
  dateOfBirth: string;
  netMonthlyIncome: number;
  drvLicenceTypeId?: number;
  drivingLicenceTypeName?: string;
  residentialAddresses?: CustomerResidentialAddress[];
}

export interface CurrentAddressStatus {
  statusDescription: string;
}

export interface CurrentEmploymentStatus {
  statusDescription: string;
  occupation: string;
}

export interface DrivingLicenceType {
  drivingLicenceTypeId: number;
  drivingLicenceTypeName: string;
}

export interface AddressMatchKey {
  key: string;
  addressLine: string;
  selected?: boolean;
}

export interface AddressMatchDetail {
  matchType: number; // 1: noMatch, 2: perfectMatch, 3: multiMatch
  keys: AddressMatchKey[];
}

export interface RunCreditCheckPayload {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  addresses: Array<{
    key?: string;
    houseName?: string;
    houseNumber?: string;
    street?: string;
    subBuilding?: string;
    postcode: string;
    town?: string;
  }>;
  searchPurpose: string;
}

export interface YearColumn {
  year: number;
  monthCount: number;
}

export interface MonthColumn {
  month: string;
  year: string;
  date: Date;
}
